'use client';
import { FC, useEffect } from 'react';
import { TypeSubFlowV2Translations } from '@/utils/translations/TypeSubFlowV2Translations';
import {
  CustomOption,
  PreviousPlay,
  StartingKit,
} from '@/hooks/useStartingKit';
import classNames from 'classnames';
import { interpolate } from '@/utils/translations';
import { useSubFlowV3Data } from '../context/SubFlowV3Context';
import { useStepTracking } from '@/hooks/subFlowAnalytics';
import { HeaderText } from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';

export interface CustomizationsProps {
  isCentered?: boolean;
  kit?: StartingKit | PreviousPlay;
  t: TypeSubFlowV2Translations;
}

function findDefaultCustomizationValue(customizableOptions: CustomOption[]) {
  if (!Array.isArray(customizableOptions)) {
    return null;
  }

  for (const option of customizableOptions) {
    if (!Array.isArray(option.option_values)) {
      continue;
    }

    const defaultValue = option.option_values.find((value) =>
      JSON.parse(value.is_default),
    );
    if (defaultValue) return defaultValue.value;
  }

  return null;
}
const Customizations: FC<CustomizationsProps> = ({ t, kit, isCentered }) => {
  const { api, data } = useSubFlowV3Data();
  const customOptions = kit?.customizable_options;
  const { onSelectedCustomOption } = useStepTracking('v3');

  const handleClick = (option: string) => {
    api.updateCustomizationId(option);
    onSelectedCustomOption(data, option);
  };

  useEffect(() => {
    //  The could be a DEFAULT Customization, so flow should include it from the beginning
    if (customOptions?.length && !data.customizationId) {
      api.updateCustomizationId(
        findDefaultCustomizationValue(customOptions) || '',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!customOptions?.length) return null;

  return (
    <div className="pb-2">
      <div className="pt-3">
        <div className="mx-auto max-w-[1200px]">
          <HeaderText
            text={interpolate(t.startingKitCustomTitle, {
              kitName: kit?.name || '',
            })}
            size={HeaderSize.H3}
            classes="font-bold pr-4"
            align={isCentered ? 'center' : 'left'}
          />

          <div
            className={classNames(
              'mx-auto flex max-w-[620px] flex-col items-center space-y-6 md:mt-1',
              {
                'text-center': isCentered,
              },
            )}
          >
            {customOptions.map((option) => {
              return (
                <div className="" key={option.option_name}>
                  <div className="mt-2 text-base leading-7">
                    {option.option_prompt}
                  </div>
                  <div className="mt-4 flex space-x-2 text-sm md:justify-center md:mt-6">
                    {option.option_values.map((value) => {
                      return (
                        <button
                          key={value.value}
                          className="flex-grow md:max-w-[145px]"
                          data-customization-option={value.value}
                          onClick={() => handleClick(value.value)}
                        >
                          <div
                            className={classNames(
                              'h-24 w-full rounded border border-gray-400 bg-white bg-contain bg-center bg-no-repeat md:h-32',
                              {
                                'box-border border-4 border-indigo':
                                  data.customizationId === value.value ||
                                  (!data.customizationId &&
                                    JSON.parse(value.is_default)),
                              },
                            )}
                            style={{
                              backgroundImage: `url(${value.image_uri})`,
                            }}
                          />
                          <div
                            className="mt-1"
                            dangerouslySetInnerHTML={{
                              __html: value.localized_label,
                            }}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Customizations;
